import { LitElement, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import sheet from './css/bb-storefront-info.css?inline'
import {
  computeStatus,
  formatOpen,
  nextOpen,
  type Holidays,
  type OperationalStatus,
  type Schedule,
} from './bbcore/opening-hours'
import { toIana } from './bbcore/rails-tz'

type Status = OperationalStatus | null

interface DaySchedule {
  enabled?: boolean
  ranges?: Array<{ from?: string; to?: string }>
}
export interface OpeningHours {
  schedule?: Record<string, DaySchedule>
  holidays?: Record<string, { name?: string; times?: Array<[string, string]> }>
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const
type Weekday = typeof WEEKDAYS[number]

function weekdayInTz(tz: string): Weekday {
  const iana = toIana(tz)
  const parts = new Intl.DateTimeFormat('en-US', {
    ...(iana ? { timeZone: iana } : {}),
    weekday: 'long',
  }).formatToParts(new Date())
  return (parts.find((p) => p.type === 'weekday')?.value || '').toLowerCase() as Weekday
}

// "Open until HH:MM" only renders when the storefront is currently open
// (per the shared computeStatus rule) AND today's schedule has a range —
// otherwise show the next opening from the schedule, or omit when there's
// no schedule data at all.
function hoursLine(
  hours: OpeningHours | null,
  status: Status,
  weekday: Weekday,
  timeZone: string,
): string | null {
  if (status === 'open') {
    const day = hours?.schedule?.[weekday]
    const closesAt = day?.enabled ? day.ranges?.[day.ranges.length - 1]?.to : undefined
    return closesAt ? `Open until ${closesAt}` : null
  }
  const next = nextOpen(
    (hours?.schedule as Schedule | undefined) || null,
    (hours?.holidays as Holidays | undefined) || null,
    timeZone,
    new Date(),
  )
  return next ? `Opens ${formatOpen(next, timeZone)}` : null
}

@customElement('bb-storefront-info')
export class BbStorefrontInfo extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property({ attribute: 'storefront-name' })
  storefrontName = ''

  // From the API. We only honour 'inactive' here — schedule can't tell
  // us a storefront is decommissioned. Open/closed is derived live from
  // `openingHours` + `timeZone` so it flips correctly mid-session.
  @property({ attribute: 'operational-status' })
  operationalStatus: Status = null

  @property({ attribute: false })
  openingHours: OpeningHours | null = null

  @property({ attribute: 'time-zone' })
  timeZone = ''

  // Re-render once a minute so derived status + "Open until …" stay fresh.
  private _tick: ReturnType<typeof setInterval> | null = null

  connectedCallback(): void {
    super.connectedCallback()
    this._tick = setInterval(() => this.requestUpdate(), 60_000)
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    if (this._tick) clearInterval(this._tick)
    this._tick = null
  }

  render() {
    const haveSchedule = !!this.openingHours?.schedule
    if (!this.operationalStatus && !haveSchedule && !this.storefrontName) return nothing

    // Single shared rule (mirror of berrybot's compute_status): the live
    // operator override wins for "closed"/"inactive"; "open" still defers
    // to the schedule when present so a past-closing storefront actually
    // reads as closed.
    const status: Status = computeStatus(
      this.operationalStatus,
      (this.openingHours?.schedule as Schedule | undefined) || null,
      (this.openingHours?.holidays as Holidays | undefined) || null,
      this.timeZone,
      new Date(),
    )
    const line = hoursLine(this.openingHours, status, weekdayInTz(this.timeZone), this.timeZone)
    const label = status === 'open' ? 'Open' : status === 'closed' ? 'Closed' : status === 'inactive' ? 'Inactive' : ''

    return html`
      ${status ? html`<span class="pill ${status}">${label}</span>` : nothing}
      ${this.storefrontName ? html`<span class="name">${this.storefrontName}</span>` : nothing}
      ${line ? html`<span class="hours">${line}</span>` : nothing}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-storefront-info': BbStorefrontInfo
  }
}
